import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import {
  evaluatePasswordRequirements,
  PASSWORD_REQUIREMENTS_COPY,
  PasswordRequirementResult,
  PasswordRequirements
} from '../../validators/password-policy';

interface DisplayRequirement {
  key: keyof PasswordRequirementResult;
  label: string;
  met: boolean;
}

/**
 * Displays a live checklist of password requirements based on the current
 * value of a password form control. Mirrors the backend password policy.
 */
@Component({
  selector: 'hfc-password-requirements',
  templateUrl: 'password-requirements.component.html',
  styleUrls: ['password-requirements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordRequirementsComponent implements OnChanges, OnDestroy {
  /** The password form control to observe. */
  @Input() control: AbstractControl | null = null;

  public requirements: DisplayRequirement[] = [];
  public allMet = false;

  private readonly destroy$ = new Subject<void>();
  private readonly controlChange$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.control) {
      this.controlChange$.next();
      if (this.control) {
        this.control.valueChanges
          .pipe(startWith(this.control.value), takeUntil(this.controlChange$), takeUntil(this.destroy$))
          .subscribe((value) => this.recompute(value));
      } else {
        this.recompute('');
      }
    }
  }

  ngOnDestroy() {
    this.controlChange$.next();
    this.controlChange$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private recompute(value: string) {
    const result: PasswordRequirements = evaluatePasswordRequirements(value);
    this.requirements = PASSWORD_REQUIREMENTS_COPY.map(({ key, label }) => ({
      key,
      label,
      met: result[key]
    }));
    this.allMet = result.allMet;
    this.cdr.markForCheck();
  }
}
